import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Pencil, X } from "lucide-react";
import { EditorShell } from "../tiq/TiqEditors";
import { api } from "../../lib/api";
import { flowColumns, setArchetypeFlow, useArchetypeFlow } from "../../lib/archetypeFlow";

// Ranked lists: rank 1 bold. `side` = "offense" | "defense" decides Responsibilities vs Matchups guarded.
export function ArchetypeFlowLists({ archetypeId, side, compact = false, color, testid = "archetype-flow" }) {
  const flow = useArchetypeFlow(archetypeId);
  const cols = flowColumns(side);
  if (!flow || !cols.some(c => (flow[c.key] || []).length)) return null;
  return (
    <div className={`grid gap-${compact ? "3" : "5"} ${compact ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 md:grid-cols-3"}`} data-testid={testid}>
      {cols.map(c => (
        <div key={c.key} data-testid={`${testid}-${c.key}`}>
          <div className={`uppercase tracking-[0.3em] font-bold ${compact ? "text-[8px]" : "text-[9px]"}`} style={{ color: color || "var(--org-accent)" }}>{c.title}</div>
          <ol className={`mt-1.5 space-y-1 ${compact ? "text-[12px]" : "text-[13px]"}`}>
            {(flow[c.key] || []).map((it, i) => (
              <li key={it.id} className={`flex items-baseline gap-2 leading-snug ${i === 0 ? "text-white font-bold" : "text-[#c0c0c0]"}`} data-testid={`${testid}-${c.key}-${it.id}`}>
                <span className="text-[9px] tabular-nums text-[#8a8a8a] w-3 shrink-0">{i + 1}</span><span>{it.name}</span>
              </li>
            ))}
            {!(flow[c.key] || []).length && <li className="text-[11px] italic text-[#575757]">None yet</li>}
          </ol>
        </div>
      ))}
    </div>
  );
}

// Admin editor: add / remove / re-rank per list; saves the whole archetype in one PUT.
export function ArchetypeFlowEditor({ archetypeId, archetypeName, side, onClose }) {
  const current = useArchetypeFlow(archetypeId);
  const cols = flowColumns(side);
  const [options, setOptions] = useState(null);
  const [lists, setLists] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.archetypeFlowOptions().then(setOptions).catch(() => toast.error("Could not load the lists")); }, []);
  useEffect(() => { if (lists === null && current !== undefined) setLists(Object.fromEntries(cols.map(c => [c.key, (current?.[c.key] || []).map(i => i.id)]))); }, [current, cols, lists]);
  if (!lists) return null;
  const nameOf = (key, id) => options?.[key]?.find(o => o.id === id)?.name || current?.[key]?.find(o => o.id === id)?.name || id;
  const move = (key, i, d) => setLists(l => { const a = [...l[key]]; const j = i + d; if (j < 0 || j >= a.length) return l; [a[i], a[j]] = [a[j], a[i]]; return { ...l, [key]: a }; });
  const remove = (key, i) => setLists(l => ({ ...l, [key]: l[key].filter((_, k) => k !== i) }));
  const add = (key, id) => id && setLists(l => l[key].includes(id) ? l : ({ ...l, [key]: [...l[key], id] }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { play_type: [], responsibility: [], matchup: [], shot_type: [], ...lists };
      const r = await api.saveArchetypeFlow(archetypeId, body);
      setArchetypeFlow(archetypeId, r);
      toast.success("Archetype links saved");
      onClose();
    } catch (e) { toast.error("Could not save", { description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };
  return (
    <EditorShell open onClose={onClose} eyebrow="Archetype links · ranked" title={archetypeName} onSave={save} saving={saving} testId="archetype-flow-editor">
      <p className="text-[12px] text-[#a0a0a0] leading-relaxed">Rank 1 defines the archetype. Use the arrows to re-rank, × to remove, and the dropdown to add. Only the links change — the lists themselves stay as they are.</p>
      {cols.map(c => (
        <div key={c.key} className="border border-white/10 bg-[#101010] p-3" data-testid={`flow-edit-${c.key}`}>
          <div className="text-[9px] uppercase tracking-[0.3em] text-[var(--org-accent)] font-bold mb-2">{c.title}</div>
          <ol className="space-y-1">
            {lists[c.key].map((id, i) => (
              <li key={id} className="flex items-center gap-2 text-[13px] text-white bg-[#141414] border border-white/10 px-2 py-1.5" data-testid={`flow-edit-${c.key}-${id}`}>
                <span className="text-[10px] tabular-nums text-[#575757] w-4">{i + 1}</span>
                <span className={`flex-1 ${i === 0 ? "font-bold" : ""}`}>{nameOf(c.key, id)}</span>
                <button onClick={() => move(c.key, i, -1)} disabled={i === 0} className="p-1 text-[#a0a0a0] hover:text-white disabled:opacity-30" data-testid={`flow-edit-${c.key}-${id}-up`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(c.key, i, 1)} disabled={i === lists[c.key].length - 1} className="p-1 text-[#a0a0a0] hover:text-white disabled:opacity-30" data-testid={`flow-edit-${c.key}-${id}-down`}><ArrowDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(c.key, i)} className="p-1 text-red-300 hover:text-red-200" data-testid={`flow-edit-${c.key}-${id}-remove`}><X className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ol>
          <select value="" onChange={e => add(c.key, e.target.value)} className="mt-2 w-full bg-[#0f0f0f] border border-white/10 px-2.5 py-2 text-[12px] text-white focus:outline-none focus:border-[var(--org-accent)]" data-testid={`flow-edit-${c.key}-add`}>
            <option value="">+ Add…</option>
            {(options?.[c.key] || []).filter(o => !lists[c.key].includes(o.id)).map(o => <option key={o.id} value={o.id}>{o.id} · {o.name}</option>)}
          </select>
        </div>
      ))}
    </EditorShell>
  );
}

// Hero strip for the Player Archetype library page (+ admin edit button when in edit mode).
export function ArchetypeFlowStrip({ archetypeId, archetypeName, side, editMode }) {
  const [editing, setEditing] = useState(false);
  const flow = useArchetypeFlow(archetypeId);
  if (!archetypeId) return null;
  return (
    <div className="mt-8 border border-white/10 bg-[#141414]/80 backdrop-blur p-5 max-w-5xl" data-testid="archetype-flow-strip">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)] font-bold">What this archetype does</div>
        {editMode && <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]" data-testid="archetype-flow-edit-btn"><Pencil className="w-3 h-3" /> Edit links</button>}
      </div>
      {flow ? <ArchetypeFlowLists archetypeId={archetypeId} side={side} /> : <div className="text-[12px] italic text-[#575757]">No links yet{editMode ? " — use Edit links to add play types, responsibilities and shot types." : "."}</div>}
      {editing && <ArchetypeFlowEditor archetypeId={archetypeId} archetypeName={archetypeName} side={side} onClose={() => setEditing(false)} />}
    </div>
  );
}
